/**
 * Frontend Template Renderer Component
 * 
 * Coordinates template rendering between Page 1 and Page 2 templates
 * with direct field mapping from the new CaseStudyResult model structure.
 * 
 * This replaces backend render_html_template lambda for improved security and performance.
 */

import { renderPage1Template } from './CaseStudyPage1.js';
import { renderPage2Template } from './CaseStudyPage2.js';
import type { CaseStudy, TextSizingConfig } from '../types/caseStudy';
import type { EditorLayout } from '../types/editor';

export const TEMPLATE_NAMES: Record<string, string> = {
  PAGE_1: 'case_study.html',
  PAGE_2: 'page_2.html'
};

interface RenderResult {
  html: string;
  template_name: string;
  template_data: {
    case_study: CaseStudy;
    text_sizing_config: TextSizingConfig;
    layout?: EditorLayout;
  };
  metadata: {
    rendered_at: string;
    template_version: string;
    model_version: string;
    renderer: string;
  };
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export const renderTemplate = (
  caseStudy: CaseStudy,
  templateName: string = TEMPLATE_NAMES.PAGE_1,
  textSizingConfig: TextSizingConfig = {} as TextSizingConfig,
  layout?: EditorLayout
): RenderResult => {
  if (!caseStudy) {
    throw new Error('Case study data is required');
  }

  if (!templateName || !Object.values(TEMPLATE_NAMES).includes(templateName)) {
    throw new Error(`Invalid template name. Must be one of: ${Object.values(TEMPLATE_NAMES).join(', ')}`);
  }

  const requiredFields = ['title', 'subtitle', 'at_a_glance', 'key_metrics', 'testimonial', 'challenges', 'objectives', 'outcomes', 'partnership_details', 'why_choose_riscpoint', 'call_to_action'];
  
  for (const field of requiredFields) {
    if (!(field in caseStudy)) {
      console.warn(`Missing required field: ${field}`);
    }
  }

  let html: string;
  try {
    switch (templateName) {
      case TEMPLATE_NAMES.PAGE_1:
        html = renderPage1Template(caseStudy, textSizingConfig, layout);
        break;
      case TEMPLATE_NAMES.PAGE_2:
        html = renderPage2Template(caseStudy, textSizingConfig, layout);
        break;
      default:
        throw new Error(`Unsupported template: ${templateName}`);
    }
  } catch (error) {
    console.error('Template rendering error:', error);
    throw new Error(`Failed to render template ${templateName}: ${(error as Error).message}`);
  }

  return {
    html,
    template_name: templateName,
    template_data: {
      case_study: caseStudy,
      text_sizing_config: textSizingConfig,
      layout
    },
    metadata: {
      rendered_at: new Date().toISOString(),
      template_version: '1.0.0',
      model_version: 'new_nested_structure',
      renderer: 'frontend'
    }
  };
};

export const validateCaseStudyStructure = (caseStudy: CaseStudy): ValidationResult => {
  const errors: string[] = [];
  
  if (!caseStudy || typeof caseStudy !== 'object') {
    errors.push('Case study must be a valid object');
    return { isValid: false, errors };
  }

  const stringFields = ['title', 'subtitle', 'at_a_glance', 'why_choose_riscpoint'];
  stringFields.forEach(field => {
    if (!caseStudy[field as keyof CaseStudy] || typeof caseStudy[field as keyof CaseStudy] !== 'string') {
      errors.push(`${field} is required and must be a string`);
    }
  });

  const arrayFields = ['challenges', 'objectives'];
  arrayFields.forEach(field => {
    if (!Array.isArray(caseStudy[field as keyof CaseStudy])) {
      errors.push(`${field} is required and must be an array`);
    }
  });

  if (!Array.isArray(caseStudy.key_metrics)) {
    errors.push('key_metrics is required and must be an array');
  } else {
    caseStudy.key_metrics.forEach((metric, index) => {
      if (!metric.title || !metric.description) {
        errors.push(`key_metrics[${index}] must have title and description`);
      }
    });
  }

  if (!Array.isArray(caseStudy.outcomes)) {
    errors.push('outcomes is required and must be an array');
  } else {
    caseStudy.outcomes.forEach((outcome, index) => {
      if (!outcome.title || !outcome.description) {
        errors.push(`outcomes[${index}] must have title and description`);
      }
    });
  }

  if (!caseStudy.testimonial || typeof caseStudy.testimonial !== 'object') {
    errors.push('testimonial is required and must be an object');
  } else {
    const testimonialFields = ['quote', 'author_name', 'author_title', 'company'];
    testimonialFields.forEach(field => {
      if (!caseStudy.testimonial[field as keyof typeof caseStudy.testimonial]) {
        errors.push(`testimonial.${field} is required`);
      }
    });
  }

  if (!caseStudy.partnership_details || typeof caseStudy.partnership_details !== 'object') {
    errors.push('partnership_details is required and must be an object');
  } else {
    if (!caseStudy.partnership_details.client_overview) {
      errors.push('partnership_details.client_overview is required');
    }
    if (!Array.isArray(caseStudy.partnership_details.partnership_details)) {
      errors.push('partnership_details.partnership_details is required and must be an array');
    } else {
      caseStudy.partnership_details.partnership_details.forEach((detail, index) => {
        if (!detail.title || !detail.description) {
          errors.push(`partnership_details.partnership_details[${index}] must have title and description`);
        }
      });
    }
  }

  if (!caseStudy.call_to_action || typeof caseStudy.call_to_action !== 'object') {
    errors.push('call_to_action is required and must be an object');
  } else {
    if (!caseStudy.call_to_action.title || !caseStudy.call_to_action.description) {
      errors.push('call_to_action must have title and description');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const getDefaultTextSizingConfig = (): Record<string, string> => ({
  challenges: 'medium',
  solutions: 'medium',
  benefits: 'medium',
  title: 'medium',
  subtitle: 'medium',
  at_glance: 'medium',
  key_metrics_title: 'medium',
  key_metrics_bullets: 'medium'
});

interface UseTemplateRendererResult {
  html: string | null;
  metadata: RenderResult['metadata'] | null;
  error: string | null;
  loading: boolean;
}

export const useTemplateRenderer = (
  caseStudy: CaseStudy,
  templateName: string,
  textSizingConfig: TextSizingConfig,
  layout?: EditorLayout
): UseTemplateRendererResult => {
  try {
    const validation = validateCaseStudyStructure(caseStudy);
    if (!validation.isValid) {
      return {
        html: null,
        error: `Invalid case study structure: ${validation.errors.join(', ')}`,
        loading: false,
        metadata: null
      };
    }

    const result = renderTemplate(caseStudy, templateName, textSizingConfig, layout);
    return {
      html: result.html,
      metadata: result.metadata,
      error: null,
      loading: false
    };
  } catch (error) {
    return {
      html: null,
      error: (error as Error).message,
      loading: false,
      metadata: null
    };
  }
};

interface TemplateRendererProps {
  caseStudy: CaseStudy;
  templateName?: string;
  textSizingConfig?: TextSizingConfig;
  layout?: EditorLayout;
  onRenderComplete?: (result: { html: string; metadata: RenderResult['metadata'] }) => void;
  onRenderError?: (error: string) => void;
  extraProps?: Record<string, any>;
}

export const TemplateRenderer = ({ 
  caseStudy, 
  templateName = TEMPLATE_NAMES.PAGE_1, 
  textSizingConfig = {} as TextSizingConfig, 
  layout,
  onRenderComplete = () => {}, 
  onRenderError = () => {},
  extraProps = {}
}: TemplateRendererProps): null => {
  const { html, error, metadata } = useTemplateRenderer(caseStudy, templateName, textSizingConfig, layout);

  if (error) {
    onRenderError(error);
    return null;
  }

  if (html && metadata) {
    onRenderComplete({ html, metadata });
    return null;
  }

  return null;
};

export default {
  renderTemplate,
  validateCaseStudyStructure,
  getDefaultTextSizingConfig,
  useTemplateRenderer,
  TemplateRenderer,
  TEMPLATE_NAMES
};
